<template lang="pug">
	.audio-tracker-envelope-control(@mousemove="onMouseMove", @mouseup="onMouseUp", @mousedown="onEnvelopeMouseDown", @dblclick.stop)
		svg(height="100%" width="100%", :id="`envelope_${track.id}`")
			polyline.envelope-line(:points="svgPoints.upper")
			polyline.envelope-line(:points="svgPoints.down")
			line.envelope-line(v-if="svgPoints.upperDefault", v-bind="svgPoints.upperDefault")
			line.envelope-line(v-if="svgPoints.downDefault", v-bind="svgPoints.downDefault")
			template(v-for="circle in svgPoints.circles")
				text.envelope-text(v-if="!circle.nextSameDirection", text-anchor="middle", :x="circle.x" :y="getTextPosition(circle)", 
					:class="{ active: activePointIndexes.includes(circle.pointIndex), pos: circle.point.value >= 1, neg: circle.point.value < 1 }") {{ gainToDb(circle.point.value) }}{{units}}
				circle.envelope-handler(:r="12" :cx="circle.x" :cy="circle.y", @mousedown="e => onHandlerMouseDown(e, circle.pointIndex)", 
					:class="{ active: activePointIndexes.includes(circle.pointIndex), 'no-events': isPencilToolActive  }")
				circle.envelope-point(:r="4" :cx="circle.x" :cy="circle.y", :class="{ active: activePointIndexes.includes(circle.pointIndex) }")
			rect.selection-rectangle(v-if="isSelectionToolActive && isMouseDown", :x="selectionPoints[0]", :y="0", :rx="0", :ry="0", :width="selectionPoints[1] - selectionPoints[0]", :height="halfHeight * 2")
</template>


<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import Loader from 'app-core/Loader.vue';
	import audioHelper from 'view/audioTracker/audio-helper';

	export default {
		translationCategory: 'audioTracker.AudioTracker',
		name: 'audio-tracker-envelope-control',
		components: { Loader },
		props: {
			track: VueTypes.object.def(null),
			tracker: VueTypes.object.def(null),
			items: VueTypes.array.def([]), //Format: [{ time: 0, value: 1 }],
			max: VueTypes.number.def(1),
			min: VueTypes.number.def(0),
			defNewValue: VueTypes.any.def(null),
			pencilToolStep: VueTypes.number.def(0.1),
			units: VueTypes.string.def('')
		},
		data() {
			return {
				isMovingPoint: false,
				points: _.cloneDeep(this.items || []),
				initPoints: _.cloneDeep(this.items || []),
				isPencilToolActive: false,
				isSelectionToolActive: false,
				selection: {
					start: null,
					end: null
				},
				isMouseDown: false,
				activePointIndexes: []
			}
		},
		computed: {
			halfHeight() {
				return this.track.__canvasHeight / 2;
			},
			pps() {
				return this.tracker.display.pixelsPerSecond;
			},
			svgPoints() {
				var upperArr = [];
				var downArr = [];
				var circles = [];

				//upper points
				if (this.points.length) {
					//upperArr.push(`0,${this.halfHeight * (this.valueToCoef(this.defNewValue || this.max) - 1)}`);
					let upperMaxPoint = this.halfHeight * (1 - this.valueToCoef(this.defNewValue || this.max));
					upperArr.push(`0,0`);
					upperArr.push(`0,${upperMaxPoint}`);
					this.points.map(f => {
						var x = f.time * this.pps;
						var y = this.halfHeight - this.halfHeight * this.valueToCoef(f.value);

						upperArr.push(`${x},${y}`);
					});
					upperArr.push(`${this.track.trimmedDuration * this.pps},${upperMaxPoint}`);
					upperArr.push(`${this.track.trimmedDuration * this.pps},0`);

					//down points
					let downMaxPoint = this.halfHeight * (1 + this.valueToCoef(this.defNewValue || this.max));					
					downArr.push(`${this.track.trimmedDuration * this.pps},${this.halfHeight * 2}`);
					downArr.push(`${this.track.trimmedDuration * this.pps},${downMaxPoint}`);
					_.clone(this.points).reverse().map(f => {
						var x = f.time * this.pps;
						var y = this.halfHeight + this.halfHeight * this.valueToCoef(f.value);

						downArr.push(`${x},${y}`);
					});
					downArr.push(`0,${downMaxPoint}`);
					downArr.push(`0,${this.halfHeight * 2}`);

					//points
					circles = this.points.map((f, pointIndex) => {
						let nextPoint = this.points[pointIndex + 1];
						let prevPoint = this.points[pointIndex - 1];
						return {
							x: f.time * this.pps,
							y: this.halfHeight - this.halfHeight * this.valueToCoef(f.value),
							pointIndex,
							point: f,
							nextSameDirection: nextPoint && prevPoint && Math.abs(f.value - prevPoint.value) <= 0.15 && (Math.sign(f.value - prevPoint.value) === Math.sign(nextPoint.value - f.value))
						}
					});
				} else {
					let defHeight = this.halfHeight * (this.defNewValue ? this.valueToCoef(this.defNewValue) : 1/3);
					upperArr.push('0,0');
					upperArr.push(`0,${defHeight}`);
					upperArr.push(`${this.track.trimmedDuration * this.pps},${defHeight}`);
					upperArr.push(`${this.track.trimmedDuration * this.pps},0`);

					downArr.push(`0,${this.halfHeight * 2}`);
					downArr.push(`0,${this.halfHeight * 2 - defHeight}`);
					downArr.push(`${this.track.trimmedDuration * this.pps},${this.halfHeight * 2 - defHeight}`);
					downArr.push(`${this.track.trimmedDuration * this.pps},${this.halfHeight * 2}`);
				}

				let upperDefault = this.defNewValue ? { 
					x1: 0, 
					y1: this.valueToCoef(this.defNewValue) * this.halfHeight, 
					x2: this.track.trimmedDuration * this.pps, 
					y2: this.valueToCoef(this.defNewValue) * this.halfHeight,
					stroke: '#00000022',
					'stroke-dasharray': 4
				} : null;

				let downDefault = this.defNewValue ? { 
					x1: 0, 
					y1: this.halfHeight * 2 - this.valueToCoef(this.defNewValue) * this.halfHeight, 
					x2: this.track.trimmedDuration * this.pps, 
					y2: this.halfHeight * 2 - this.valueToCoef(this.defNewValue) * this.halfHeight,
					stroke: '#00000022',
					'stroke-dasharray': 4
				} : null;

				return {
					upper: upperArr.join(' '),
					down: downArr.join(' '),
					circles,
					upperDefault,
					downDefault
				}
			},
			selectionPoints() {
				let min = Math.min(this.selection.start, this.selection.end);
				let max = Math.max(this.selection.start, this.selection.end);

				return [min, max];
			}
		},
		watch: {
			points: {
				handler() {
					this.$save();
				},
				deep: true
			}
		},
		mounted() {
			this.$onMouseMove = (e) => {
				this.onMouseMove(e, this);
			}
			document.addEventListener('mouseup', this.onMouseUp);
			document.addEventListener('mousedown', this.onMouseDown);
			document.addEventListener('mousemove', this.$onMouseMove);
			document.addEventListener('keyup', this.onKeyUp);

			this.$eventsBus.$on('track-envelope-clear', this.clearPoints);
			this.$eventsBus.$on('track-envelope-save', this.save);
			this.$eventsBus.$on('track-envelope-cancel', this.cancel);
			this.$eventsBus.$on('track-envelope-pencil-tool', this.togglePencilTool);
			this.$eventsBus.$on('track-envelope-selection-tool', this.toggleSelectionTool);
			
			this.$save = _.debounce(this.save, 300, {
				'leading': false,
				'trailing': true
			});

			if (__DEBUG__)
				window.envelopeControl = this;
		},
		destroyed() {
			document.removeEventListener('mouseup', this.onMouseUp);
			document.removeEventListener('mousedown', this.onMouseDown);
			document.removeEventListener('mousemove', this.$onMouseMove);
			document.removeEventListener('keyup', this.onKeyUp);
			delete this.$onMouseMove;

			this.$eventsBus.$off('track-envelope-clear', this.clearPoints);
			this.$eventsBus.$off('track-envelope-save', this.save);
			this.$eventsBus.$off('track-envelope-cancel', this.cancel);
			this.$eventsBus.$off('track-envelope-pencil-tool', this.togglePencilTool);
			this.$eventsBus.$off('track-envelope-selection-tool', this.toggleSelectionTool);

			delete this.$save;
		},		
		methods: {
			toggleSelectionTool(v) {
				this.isSelectionToolActive = v;
				this.isPencilToolActive = false;
			},
			togglePencilTool(v) {
				this.isPencilToolActive = v;
				this.isSelectionToolActive = false;
			},
			getTextPosition(circle) {
				return Math.max(10, circle.y + (circle.point.value > 1 ? -10 : 15));
			},
			gainToDb(v) {
				return (v > 1 ? '+' : '') + this.$Data.formatNumber(audioHelper.gainToDb(Math.max(0.001, v)), '##0.#');
			},
			valueToCoef(v) {
				return (v - this.min) / (this.max - this.min);
			},
			coefToValue(c) {
				return Math.max(this.min, Math.min(this.max, (this.max - this.min) * c + this.min));
			},
			save() {
				this.$emit('save', this.points, !!this.points.length);
			},
			cancel() {
				if (JSON.stringify(this.initPoints) !== JSON.stringify(this.points))
					this.$emit('save', this.initPoints, !!this.initPoints.length);
			},
			clearPoints() {
				this.points = [];
			},
			onKeyUp(e) {
				if (__DEBUG__)
					console.debug('Key press', e);
				if (e?.keyCode === 46 && this.activePointIndexes.length) {
					this.removeActivePoint();
				}
			},
			onMouseDown() {
				this.activePointIndexes = [];
			},
			onMouseMove(e, sender) {
				if (sender !== this) return;
				if (this.isMovingPoint) {
					if (!this.$movingPointInitialPositon) return;

					var pointBefore = this.points[this.activePointIndexes[0] - 1] || { time: 0, value: this.defNewValue || 1 };
					var pointAfter = this.points[this.activePointIndexes.slice(-1)[0] + 1] || { time: this.track.trimmedDuration, value: this.defNewValue || 1 };
					this.activePointIndexes.map(pIdx => {
						var point = this.points[pIdx];
						if (point) {
							var deltaTime = e.movementX / this.pps;
							var deltaValue = e.movementY / this.halfHeight;
							
							let totalMovingDeltaX = Math.abs(e.offsetX - this.$movingPointInitialPositon.x);
							let totalMovingDeltaY = Math.abs(e.offsetY - this.$movingPointInitialPositon.y);
							
							let startLimit = 5;
							if (totalMovingDeltaX > startLimit || totalMovingDeltaY > startLimit) {
								if (!this.$movingPointDirection) {
									this.$movingPointDirection = totalMovingDeltaX > totalMovingDeltaY ? 'x' : 'y';
								}
								this.$lastMovingTime = new Date();
								this.$movingPointInitialPositon = { x: e.offsetX, y: e.offsetY };
							} else {
								if (this.$moment().diff(this.$lastMovingTime, 'miliseconds') > 500) {
									this.$movingPointDirection = null;	
								}
							}

							if (this.$movingPointDirection === 'x') {
								point.time = Math.min(pointAfter.time - 0.0001, Math.max(pointBefore.time + 0.0001, point.time + deltaTime));
							}
							
							if (this.$movingPointDirection === 'y') {
								point.value = this.coefToValue(this.valueToCoef(point.value) - deltaValue);
							}
						}
					});
				} else if (this.isPencilToolActive && this.isMouseDown) {
					let point = this.points[this.activePointIndexes[0]];
					if (!point) return;
					
					let { time, value } = this.getTimeValue(e.offsetX, e.offsetY);

					if (Math.abs(point.value - value) >= this.pencilToolStep && point.time < time) {
						this.activePointIndexes = [this.createNewPoint(time, value)];
					}
				} else if (this.isSelectionToolActive) {
					this.selection.end = e.offsetX;
				}

				e.stopPropagation();
				e.preventDefault();
			},
			onHandlerMouseDown(e, pointIndex) {
				if (__DEBUG__)
					console.debug('onHandlerMouseDown');
				if (!this.activePointIndexes.includes(pointIndex)) {
					this.activePointIndexes = [pointIndex];
				}
				this.isMovingPoint = true;
				this.$movingPointInitialPositon = { x: e.offsetX, y: e.offsetY };
				this.$movingPointDirection = null;
				e.stopPropagation();
				e.preventDefault();
			},
			onEnvelopeMouseDown(e) {
				this.isMouseDown = true;

				if (this.isSelectionToolActive) {
					this.selection.start = e.offsetX;
					this.selection.end = e.offsetX;
				} else {
					let { time, value } = this.getTimeValue(e.offsetX, e.offsetY);
					this.activePointIndexes = [this.createNewPoint(time, value)];
					this.isMovingPoint = !this.isPencilToolActive;
				}
				
				e.stopPropagation();
				e.preventDefault();
			},
			getTimeValue(x, y) {
				let time = Math.min(this.track.trimmedDuration, Math.max(0, x / this.pps));
				let value = this.coefToValue(1 - Math.min(y / this.halfHeight, 1));

				return {
					time,
					value
				}
			},
			createNewPoint(time, value) {
				this.points.push({ time, value });
				this.points = _.orderBy(this.points, 'time');
				return _.findIndex(this.points, v => v.time === time && v.value === value);
			},
			onMouseUp() {
				if (this.isSelectionToolActive && !this.isMovingPoint && this.isMouseDown) {
					this.processSelection(this.selectionPoints);
				}

				this.isMovingPoint = false;
				this.isMouseDown = false;
			},
			removeActivePoint() {
				if (this.activePointIndexes.length) {
					[...this.activePointIndexes].reverse().map(i => {
						this.points.splice(i, 1);
					});
					
					this.activePointIndexes = [];
				}
			},
			processSelection(range) {
				this.activePointIndexes = this.svgPoints.circles.filter(f => f.x >= range[0] && f.x <= range[1]).map(f => f.pointIndex);
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.audio-tracker-envelope-control {
		position: absolute;
		background: #00000020;
		left: 0px;
		right: 0px;
		top: 0px;
		bottom: 0px;
		z-index: 10;

		.envelope-point {
			fill: #dcdcdc;
			stroke: $base-accent;
			stroke-width: 2;
			pointer-events: none;
			
			&.active {
				fill: $base-accent;
			}
		}

		.envelope-line {
			fill: #00000011;
		}

		.envelope-handler {
			fill: #00000000;
			
			&.active {
				fill: #ffffff44;
			}

			&.no-events {
				pointer-events: none;
			}
		}

		.envelope-text {
			font-size: 10px;
			filter: drop-shadow( 0px 0px 1px rgba(0, 0, 0, 0.7));
			pointer-events: none;

			&.active {
				font-weight: 600;
			}

			&.pos {
				fill: rgb(119, 255, 131);
			}

			&.neg {
				fill: #fcff50;
			}
		}

		.selection-rectangle {
			fill: #ffffff33;
			border: 1px solid #333;
		}
	}
</style>
